import { Table } from '@/components/commons';
import { useAuth } from '@/providers/Auth';
import { AdminUserService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { IAdminUser, IUpdateAdminAccessRequest } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  Alert,
  Button,
  Modal,
  Select,
  Switch,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  ADMIN_PERMISSION_LABELS,
  ADMIN_ROLE_LABELS,
  ADMIN_ROLE_OPTIONS,
  ADMIN_USER_STATE_LABELS,
  accessErrorLabel,
  accessLockReason,
  adminRoleOf,
  adminUserState,
  compareDate,
  countActiveAdmins,
  formatElapsed,
} from './adminUserView';

const { Title, Text } = Typography;

interface IProps {
  title?: string;
}

/** 日時と「3日前」。記録が無ければ「記録なし」 */
const DateTimeCell: React.FC<{ value: string | null }> = ({ value }) =>
  value ? (
    <Tooltip title={moment(value).format('YYYY/MM/DD HH:mm')}>
      <div>{moment(value).format('YYYY/MM/DD')}</div>
      <div className='text-fg-muted text-[12px]'>{formatElapsed(value)}</div>
    </Tooltip>
  ) : (
    <span className='text-fg-muted'>記録なし</span>
  );

/** 管理ユーザーの一覧。停止・再開と、管理者／閲覧専用の切り替えができる（登録は CLI の create-admin） */
export const AdminUserList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const query = useQuery([ENDPOINT.ADMIN_USER.GET_LIST], () =>
    AdminUserService.getList(),
  );

  const users = query.data?.data ?? [];
  const activeAdminCount = countActiveAdmins(users);

  const refresh = () =>
    queryClient.invalidateQueries([ENDPOINT.ADMIN_USER.GET_LIST]);

  const { mutate: updateAccess, isLoading: isUpdating } = useMutation(
    (payload: { id: number; body: IUpdateAdminAccessRequest }) =>
      AdminUserService.updateAccess(payload.id, payload.body),
    {
      onSuccess: () => {
        notificationMessage({
          message: '管理ユーザーを更新しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refresh();
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: accessErrorLabel(error?.error),
          type: NOTIFY_STATUS.ERROR,
        });
        refresh();
      },
    },
  );

  /** 変更の前に確認を挟む。停止と権限変更はどちらも管理画面への入り方に効くため */
  const confirmChange = (user: IAdminUser, body: IUpdateAdminAccessRequest) => {
    const content = body.status
      ? body.status === 'deaActive'
        ? '停止すると、このユーザーは管理画面を使えなくなります。'
        : '再開すると、このユーザーは管理画面を使えるようになります。'
      : `ロールを「${ADMIN_ROLE_LABELS[adminRoleOf(user) ?? '']?.label ?? '-'}」から「${ADMIN_ROLE_LABELS[body.role ?? '']?.label}」に変更します。`;

    Modal.confirm({
      title: `${user.email ?? '-'} を変更しますか？`,
      content,
      okText: '変更する',
      cancelText: 'キャンセル',
      onOk: () => updateAccess({ id: user.id, body }),
    });
  };

  const columns: TableColumnsType<IAdminUser> = [
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      width: 260,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: 'ロール',
      dataIndex: 'roles',
      width: 120,
      render: (roles: string[]) =>
        roles
          .filter((r) => ADMIN_ROLE_LABELS[r])
          .map((r) => (
            <Tag key={r} color={ADMIN_ROLE_LABELS[r].color}>
              {ADMIN_ROLE_LABELS[r].label}
            </Tag>
          )),
    },
    {
      title: '権限',
      dataIndex: 'permissions',
      width: 220,
      render: (keys: string[]) =>
        keys
          .filter((k) => ADMIN_PERMISSION_LABELS[k])
          .map((k) => ADMIN_PERMISSION_LABELS[k])
          .join('・') || '-',
    },
    {
      title: '状態',
      key: 'state',
      width: 96,
      align: 'center',
      render: (_: unknown, r: IAdminUser) => {
        const state = ADMIN_USER_STATE_LABELS[adminUserState(r)];

        return <Tag color={state.color}>{state.label}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      render: (_: unknown, r: IAdminUser) => {
        const reason = accessLockReason(r, {
          currentUserId: currentUser?.id,
          activeAdminCount,
        });
        const role = adminRoleOf(r);
        const locked = !!reason || isUpdating;

        return (
          <div className='flex items-center gap-sm'>
            <Tooltip title={reason}>
              <span>
                <Switch
                  size='small'
                  checked={adminUserState(r) === 'active'}
                  disabled={locked}
                  checkedChildren='有効'
                  unCheckedChildren='停止'
                  onChange={(checked) =>
                    confirmChange(r, {
                      status: checked ? 'active' : 'deaActive',
                    })
                  }
                />
              </span>
            </Tooltip>
            <Tooltip title={reason}>
              <span>
                <Select
                  size='small'
                  className='w-[120px]'
                  value={role}
                  options={ADMIN_ROLE_OPTIONS}
                  disabled={locked || !role}
                  onChange={(value: string) =>
                    confirmChange(r, {
                      role: value as IUpdateAdminAccessRequest['role'],
                    })
                  }
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'ラストログイン',
      dataIndex: 'adminLastSignInAt',
      width: 140,
      sorter: (a, b) => compareDate(a.adminLastSignInAt, b.adminLastSignInAt),
      render: (v: string | null) => <DateTimeCell value={v} />,
    },
    {
      title: '最終アクセス',
      dataIndex: 'adminLastAccessAt',
      width: 140,
      defaultSortOrder: 'descend',
      sorter: (a, b) => compareDate(a.adminLastAccessAt, b.adminLastAccessAt),
      render: (v: string | null) => <DateTimeCell value={v} />,
    },
    {
      title: '登録日',
      dataIndex: 'createdAt',
      width: 110,
      render: (v: string) => moment(v).format('YYYY/MM/DD'),
    },
  ];

  return (
    <div className='flex flex-col gap-xl pb-xl mx-auto w-full max-w-[1280px]'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div>
        <Title level={4} className='!mb-xs'>
          管理ユーザー
        </Title>
        <Text type='secondary' className='text-[12px]'>
          管理画面にログインできるユーザーです。停止・再開と、管理者／閲覧専用の切り替えはこの画面で行います。
          新規の登録は
          CLI（create-admin）で行います。自分自身と、最後の有効な管理者は変更できません。
          ラストログイン・最終アクセスは記録を始めてからの分だけで、最終アクセスは1時間単位です。
        </Text>
      </div>

      {query.isError ? (
        <Alert
          type='error'
          showIcon
          message='取得できませんでした'
          description={
            <Button size='small' onClick={() => query.refetch()}>
              再読み込み
            </Button>
          }
        />
      ) : (
        <Table
          loading={query.isLoading}
          scroll={{ x: 1346 }}
          rowKey='id'
          columns={columns}
          dataSource={users}
          locale={{ emptyText: '管理ユーザーはいません' }}
        />
      )}
    </div>
  );
};
